import { GlobalBannerConnected } from '@ths/shared/store/slices/banner';
import { Outlet, useLocation } from 'hotel/navigation';
import { useState } from 'react';
import { Toaster } from 'react-hot-toast';

import {
  UserRole,
  useHotelBootstrapQuery,
  useInitializeQuery,
} from '../generated/graphql';
import { useInactiveClientBanner } from '../hooks/useInactiveClientBanner';
import { setClientConfig } from '../store/clientConfigSlice';
import { EnumsState, setEnums } from '../store/enumsSlice';
import { useAppDispatch, useAppSelector } from '../store/store';
import { setUnknownGuest } from '../store/unknownGuestSlice';
import { setUser } from '../store/userSlice';
import { hasHotelPortalAccess } from '../utilities/clientPortalRoles';

import Card from './Card';
import ErrorMessage from './ErrorMessage';
import Footer from './Footer';
import Header from './Header';
import Loading from './Loading';
import { Restricted } from './Restricted';

const Wrapper = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [initializeReady, setInitializeReady] = useState(false);
  const [hotelBootstrapReady, setHotelBootstrapReady] = useState(false);

  // Enable inactive client banner
  useInactiveClientBanner();

  // Define background colors for specific routes
  const backgroundColor = (() => {
    switch (location.pathname) {
      case '/dashboard':
        return 'bg-gradient-to-b from-backgroundLightGray to-[#D5E1F2]';
      default:
        return '';
    }
  })();

  // Hide footer on hotel search pages
  const hideFooter = location.pathname.startsWith('/search');

  const {
    data: initializeData,
    loading: initializeLoading,
    error: initializeError,
  } = useInitializeQuery({
    onError: (error) => {
      console.error(error);
    },
    onCompleted: (data) => {
      dispatch(setUser(data.clientUser));
      const initialEnums: EnumsState = {};
      initialEnums.featureFlags = {
        revision: data.featureFlags.revision,
        flags: data.featureFlags.flags.reduce(
          (acc, flag) => ({
            ...acc,
            [flag.key]: flag.enabled,
          }),
          {} as Record<string, boolean>,
        ),
      };
      dispatch(setEnums(initialEnums));
      setInitializeReady(true);
    },
  });

  const roles = initializeData?.clientUser.roles ?? [];
  const hasHotelPortalRole = hasHotelPortalAccess(roles);

  const { loading: hotelLoading, error: hotelError } = useHotelBootstrapQuery({
    skip: !hasHotelPortalRole,
    onError: (error) => {
      console.error(error);
    },
    onCompleted: (data) => {
      const { clientRequiredFields, clientSubgroups, unknownGuest, ...enums } =
        data;

      // Set unknown guest if available
      if (unknownGuest) {
        dispatch(setUnknownGuest(unknownGuest));
      }

      const enumsForState: EnumsState = {};

      Object.keys(enums).forEach((enumType) => {
        // eslint-disable-next-line
        // @ts-ignore
        const enumData = data[enumType];
        if (
          enumData &&
          enumData.enumValues &&
          Array.isArray(enumData.enumValues)
        ) {
          const options = enumData.enumValues.map(
            ({ name, description }: { name: string; description: string }) => {
              return {
                value: name,
                label: description,
              };
            },
          );

          enumsForState[enumType] = options;
        }
      });

      if (clientSubgroups && Array.isArray(clientSubgroups)) {
        enumsForState.clientSubgroups = clientSubgroups.map((subgroup) => ({
          value: subgroup,
          label: subgroup,
        }));
      }

      dispatch(setEnums(enumsForState));

      if (clientRequiredFields) {
        dispatch(
          setClientConfig({
            pod: clientRequiredFields.pod,
            requiredFields: clientRequiredFields.requiredFields.map(
              (field) => ({
                value: field.value,
                label: field.label,
                readOnly: field.readOnly,
                scope: field.scope,
              }),
            ),
          }),
        );
      }
      setHotelBootstrapReady(true);
    },
  });

  const error = initializeError ?? hotelError;
  if (error) {
    return <ErrorMessage errorMessage={error.message} />;
  }

  // Apollo can finish loading before its deferred onCompleted callbacks apply
  // portal state. Keep child routes closed until both store updates finish.
  if (
    initializeLoading ||
    !initializeReady ||
    (hasHotelPortalRole && (hotelLoading || !hotelBootstrapReady))
  ) {
    return (
      <div className="flex h-full flex-col">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col bg-gray-50">
      <Header />
      <GlobalBannerConnected
        useAppSelector={useAppSelector}
        useAppDispatch={useAppDispatch}
      />
      <Toaster
        containerStyle={{
          position: 'absolute',
          top: 70,
          left: 0,
          bottom: 0,
          right: 0,
          zIndex: 100,
        }}
        toastOptions={{
          duration: 3000,
        }}
      />
      <main className={`relative flex-1 ${backgroundColor}`}>
        <div className="relative mx-auto">
          <Restricted
            roles={[
              UserRole.ClientAdmin,
              UserRole.ClientUser,
              UserRole.ClientReadOnly,
              UserRole.ClientTraveler,
            ]}
            fallback={
              <Card>
                <div>Not Authorized!</div>
              </Card>
            }
          >
            <Outlet />
          </Restricted>
        </div>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default Wrapper;
